import { motion } from 'framer-motion';
import { HOME_V2_COLORS } from './colors';
import PlayMomentsLogo from '../../../assets/icons/home-v2/header-playmoments.svg';
import CloudCharacter from '../../../assets/icons/home-v2/header-cloud.svg';
import DefaultAvatar from '../../../assets/icons/avatar.svg';


export const HEADER_HEIGHT = 77; // Mobile: 77px (61px logo + 16px py-2) | sm+: 85px (61px logo + 24px py-3)
export const HEADER_HEIGHT_MOBILE = 77;
export const HEADER_HEIGHT_DESKTOP = 85;

/**
 * HomeV2Header Component
 *
 * Fixed header for Home V2 design.
 * Features:
 * - Cyan background (#01A7C8)
 * - "Play Moments" hand-drawn logo
 * - Cloud character mascot (interactive with smooth spring animations when onCloudClick provided)
 * - User avatar (circular, 36px)
 *
 * Design specs:
 * - Height: auto (determined by content)
 * - Logo positioned left (~24px padding)
 * - Cloud character center-right (clickable with hover/tap animations)
 * - Avatar in 48px tall Avatar Bar container for proper vertical alignment
 */

type HeaderProps = {
  bottomRenderProp?: React.ReactNode;
  bottomBorderwidth?: number;
  showCloudLogo?: boolean;
  /** Override header background color (for time-of-day theming) */
  backgroundColor?: string;
  /** Whether the check-in transition is active (triggers fade-out) */
  isTransitioning?: boolean;
  /** Show user avatar on the right side of the header */
  showAvatar?: boolean;
  /** Click handler for the cloud mascot*/
  onCloudClick?: () => void;
}


{/* Header placeholder - Match header's box model exactly Mirrors header's structure: outer padding container + inner content */ }
export function HomeV2HeaderPlaceholder() {
  return (
    <div className="py-2 sm:py-3 flex items-center" style={{ height: 'auto' }}>
      <div style={{ height: '61px' }} /> {/* Inner content spacer */}
    </div>
  );
}

export default function HomeV2Header(props: HeaderProps) {

  const {
    showCloudLogo = true,
    isTransitioning = false,
    showAvatar = true,
    onCloudClick,
  } = props;

  return (
    <header
      className="fixed top-0 left-0 right-0 z-30"
      style={{
        height: 'auto',
        backgroundColor: props.backgroundColor ?? HOME_V2_COLORS.headerBackground,
        transition: isTransitioning
          ? 'background-color 0.5s ease-in-out, opacity 0.25s ease'
          : 'background-color 0.5s ease-in-out',
        transitionDelay: isTransitioning ? '0.20s' : '0s',
        opacity: isTransitioning ? 0 : 1,
      }}
    >
      <div className="max-w-md lg:max-w-[640px] mx-auto px-6 sm:px-8 md:px-10 py-2 sm:py-3 flex items-center justify-between"
        style={{
          backgroundColor: props.backgroundColor ?? HOME_V2_COLORS.headerBackground,
          transition: 'background-color 0.5s ease-in-out',
        }}
      >
        {/* Play Moments Logo - Left */}
        <div className="flex-shrink-0">
          <img
            src={PlayMomentsLogo}
            alt="Play Moments"
            style={{
              width: '95px',
              height: '61px',
            }}
          />
        </div>

        {/* Right section: Cloud + Avatar */}
        <div className="flex items-center gap-4 sm:gap-5 lg:gap-6">
          {/* Cloud Character - Interactive when onCloudClick is provided */}
          {showCloudLogo && (
            onCloudClick ? (
              <motion.button
                onClick={onCloudClick}
                whileHover={{ scale: 1.08, y: -2 }}
                whileTap={{ scale: 0.95, y: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 17
                }}
                className="flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 rounded"
                aria-label="Cloud character"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <img
                  src={CloudCharacter}
                  alt=""
                  style={{
                    width: '57px',
                    height: '36px',
                    display: 'block',
                  }}
                />
              </motion.button>
            ) : (
              <div className="flex-shrink-0" aria-hidden="true">
                <img
                  src={CloudCharacter}
                  alt=""
                  style={{
                    width: '57px',
                    height: '36px',
                  }}
                />
              </div>
            )
          )}

          {/* Avatar Bar - Container for proper vertical alignment */}
          {
            showAvatar ? (
              <div className="flex items-center justify-center gap-2" style={{ height: '48px' }}>
                {/* User Avatar - Using default avatar SVG pattern */}
                <div
                  className="flex-shrink-0 rounded-full overflow-hidden"
                  style={{
                    width: '36px',
                    height: '36px',
                  }}
                >
                  <img
                    src={DefaultAvatar}
                    alt="User avatar"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            ) : null
          }
        </div>
      </div>
    </header>
  );
}
